import React, { FC, useEffect, useState } from 'react';
import { Box, Button, MenuItem, Select, TextField, Typography } from '@mui/material';
import { AwardModel, IAwardsHolder } from 'entities/competitions-b2b/types';
import { useAppDispatch } from 'app/store';
import { toast } from 'react-toastify';
import { getAwardsThunk, updateAwardsThunk } from './thunk';
import { CreatedPrize } from '../createNew/ui';
import { IPrizeListFeature, IRequestData } from './types';

const PrizeListFeature: FC<IPrizeListFeature> = ({ competitionId, refetchCompetition }) => {
  const [inputValueFrom, setInputValueFrom] = useState<string | number>();
  const [inputValueTo, setInputValueTo] = useState<string | number>();
  const [state, setState] = useState<IAwardsHolder[]>([]);
  const dispatch = useAppDispatch();

  const onSubmit = async () => {
    const requestData: IRequestData = {
      body: state.map((item) => ({
        ...item,
        place_start: item.place_start ?? item.award_place,
        place_end: item.place_end === 0 ? null : item.place_end,
      })),
      competition_id: competitionId,
    };
    await dispatch(updateAwardsThunk(requestData)).then(() => {
      refetchCompetition();
    });
    await dispatch(getAwardsThunk(competitionId)).then((ans) => {
      const awardsData: any = ans.payload;
      const newAwardsHolders = [];
      if (awardsData?.length < 3) {
        for (let i = (awardsData?.length || 0) + 1; i <= 3; i++) {
          newAwardsHolders.push({
            place_start: i,
            place_end: null,
            awards: [],
          });
        }
      }
      setState([...awardsData, ...newAwardsHolders]);
    });
  };

  useEffect(() => {
    dispatch(getAwardsThunk(competitionId)).then((ans) => {
      const awardsData: any = ans.payload;
      const newAwardsHolders = [];
      if (awardsData?.length < 3) {
        for (let i = (awardsData?.length || 0) + 1; i <= 3; i++) {
          newAwardsHolders.push({
            place_start: i,
            place_end: null,
            awards: [],
          });
        }
      }
      setState([...awardsData, ...newAwardsHolders]);
    });
  }, []);

  const deleteAwardPlace = (awardIndex: number) => {
    if (state.length !== awardIndex + 1) {
      toast.error('Для удаления этого призового места сначала удалите предыдущие');
      return;
    }
    const filteredAwards = (prev: IAwardsHolder[]) => prev.filter((_, index) => index !== awardIndex);
    setState((prev) => filteredAwards(prev));
  };

  const maxPlaceFrom = (state as IAwardsHolder[]).reduce(
    (prev, item) => Math.max(prev, Number(item.place_end || 3)),
    0,
  );
  const maxPlaceTo =
    inputValueTo !== 0 && Number(inputValueFrom) > maxPlaceFrom ? Number(inputValueFrom) - 1 : maxPlaceFrom;
  const errorFrom = inputValueFrom !== 0 && Number(inputValueFrom) <= maxPlaceFrom;
  const errorTo =
    inputValueTo !== 0 &&
    Number(inputValueTo) < (Number(inputValueFrom) > maxPlaceFrom ? Number(inputValueFrom) : maxPlaceFrom);

  return (
    <>
      <Typography
        mt={1}
        mb={1}
      >
        Призовые места
      </Typography>
      {state.map((field, index) => (
        <Box
          key={index}
          sx={{ width: '100%' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
            <Typography>
              {field.award_place
                ? field.award_place
                : index > 2
                ? `${field.place_start}-${field.place_end}`
                : field.place_start}{' '}
              место
            </Typography>
            {index > 2 ? (
              <Button
                color="error"
                onClick={() => deleteAwardPlace(index)}
              >
                Удалить
              </Button>
            ) : null}
          </Box>
          <AwardItemFeature
            index={index}
            item={field}
            setState={setState}
            state={state}
          />
        </Box>
      ))}
      <Button
        disabled={!inputValueFrom || !inputValueTo || errorFrom || errorTo}
        onClick={() => {
          setState([
            ...state,
            {
              place_start: state.length > 2 ? parseInt(inputValueFrom as string) : state.length + 1,
              place_end: state.length > 2 ? parseInt(inputValueTo as string) : undefined,
              awards: [],
            },
          ]);
          setInputValueFrom(inputValueFrom ? parseInt(inputValueFrom.toString()) + 1 : 4);
          setInputValueTo(inputValueTo ? parseInt(inputValueTo.toString()) + 1 : 4);
        }}
      >
        Добавить
      </Button>
      {state.length > 2 && (
        <div style={{ marginBottom: '24px', width: '50%', display: 'flex', marginTop: '16px' }}>
          <TextField
            type="number"
            label="Место, от"
            placeholder=""
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: 1,
              max: 9999,
              step: 1,
              onInput: (e: any) => {
                if (e.target.value.length > 4) e.target.value = e.target.value.slice(0, 4);
                if (e.target.value === '0') e.target.value = '';
              },
              onKeyDown: (e: any) => {
                if (e.key === '-' || e.key === 'e') e.preventDefault();
              },
            }}
            sx={{
              marginBottom: '24px',
              marginRight: '20px',
              width: '50%',
            }}
            error={errorFrom}
            helperText={errorFrom ? `Значение должно быть больше ${maxPlaceFrom}` : ''}
            value={inputValueFrom}
            onChange={(event: any) => {
              const { value } = event.target;
              setInputValueFrom(value);
            }}
          />
          <TextField
            type="number"
            label="Место, до"
            placeholder=""
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: 1,
              max: 9999,
              step: 1,
              onInput: (e: any) => {
                if (e.target.value.length > 4) e.target.value = e.target.value.slice(0, 4);
                if (e.target.value === '0') e.target.value = '';
              },
              onKeyDown: (e: any) => {
                if (e.key === '-' || e.key === 'e') e.preventDefault();
              },
            }}
            sx={{
              marginBottom: '24px',
              width: '50%',
            }}
            error={errorTo}
            helperText={errorTo ? `Значение должно быть больше ${maxPlaceTo}` : ''}
            value={inputValueTo}
            onChange={(event: any) => {
              const { value } = event.target;
              setInputValueTo(value);
            }}
          />
        </div>
      )}
      <Box
        sx={{
          display: 'flex',
          marginTop: '30px',
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            onSubmit();
          }}
          sx={{ textTransform: 'none' }}
        >
          Сохранить
        </Button>
      </Box>
    </>
  );
};

const AwardItemFeature: FC<{
  index: number;
  item: IAwardsHolder;
  setState: (data: IAwardsHolder[]) => void;
  state: IAwardsHolder[];
}> = ({ index, item, setState, state }) => {
  const [awards, setAwards] = useState<Array<AwardModel> | []>([]);

  const updateItemTypeAwards = (itemIndex: number, evValue: string) => {
    const updatedAwards = [...awards];
    if (updatedAwards[itemIndex]) {
      updatedAwards[itemIndex].type = evValue;
    }
    setAwards(updatedAwards);
  };

  const updateItemCoinsAwards = (itemIndex: number, evValue: string) => {
    const updatedAwards = [...awards];
    updatedAwards[itemIndex].coins = parseInt(evValue);
    setAwards(updatedAwards);
  };

  const updateItemPrizeIdAward = (itemIndex: number, evValue: string) => {
    const updatedAwards = [...awards];
    if (updatedAwards[itemIndex]) {
      updatedAwards[itemIndex].prize_id = parseInt(evValue);
    }
    setAwards(updatedAwards);
  };

  const updateMainAward = (indexToMakeMain: number) => {
    const updatedAwards = awards.map((award) => ({
      ...award,
      is_main: false,
    }));
    if (updatedAwards[indexToMakeMain]) {
      updatedAwards[indexToMakeMain].is_main = true;
    }
    setAwards(updatedAwards);
  };

  const deleteAward = (awardIndex: number) => setAwards(awards.filter((_, index) => index !== awardIndex));

  useEffect(() => {
    setState(
      state.map((item, itemIndex) => {
        let newItem = item.awards.map((award) => award);
        if (itemIndex === index) {
          newItem = awards;
        }
        return { ...item, awards: newItem };
      }),
    );
  }, [awards]);

  useEffect(() => {
    setAwards([
      ...awards,
      ...item.awards.map((award) => {
        const newAward = award;
        if (award.prize_id && award.prize_id !== 0) {
          newAward.type = 'PRIZE';
        } else {
          newAward.type = 'COINS';
        }
        return newAward;
      }),
    ]);
  }, []);

  return (
    <>
      {awards.map((award, index) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Select
            sx={{ marginBottom: '24px' }}
            fullWidth
            value={awards[index]?.type || 'PRIZE'}
            onChange={(event) => {
              updateItemTypeAwards(index, event.target.value as string);
            }}
          >
            <MenuItem value="COINS">Монеты</MenuItem>
            <MenuItem value="PRIZE">Товар</MenuItem>
          </Select>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {award.type === 'COINS' ? (
              <TextField
                key={index}
                type="text"
                label="Количество монет"
                placeholder=""
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  marginBottom: '24px',
                  width: '70%',
                }}
                required
                value={awards[index]?.coins}
                onChange={(event: any) => {
                  const { value } = event.target;
                  updateItemCoinsAwards(index, value);
                }}
              />
            ) : (
              <CreatedPrize
                index={index}
                updateItemPrizeIdAward={updateItemPrizeIdAward}
                prizeId={award.prize_id}
              />
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
                width: '30%',
                height: '70%',
              }}
            >
              {awards[index]?.is_main ? (
                <Button disabled>Основной</Button>
              ) : (
                <Button
                  onClick={() => {
                    updateMainAward(index);
                  }}
                >
                  Сделать основным
                </Button>
              )}
              <Button
                color="error"
                onClick={() => {
                  deleteAward(index);
                }}
              >
                Удалить
              </Button>
            </div>
          </div>
        </div>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
        <Button
          onClick={() =>
            setAwards([
              ...awards,
              {
                coins: null,
                type: null,
                prize_id: null,
                is_main: false,
              },
            ])
          }
        >
          Добавить приз
        </Button>
      </Box>
    </>
  );
};

export { PrizeListFeature };
